import { memo, useState } from 'react';
import { Segment } from 'semantic-ui-react';
import { Form, Input, Button, Typography, message } from 'antd';

import ShippingForm from 'pages/auctions/CreateAuction/components/AuctionForm/TradeForm/ShippingForm/ShippingForm';

import { User } from 'models/user/User';
import { Shipping, ShippingWay } from 'models/auction/Shipping';

import styles from './EditSellerInfoForm.module.css';
import { patchUser } from 'apis/user';
import useAppSelector from 'hooks/useAppSelector';
import { setUserState } from 'features/userSlice';
import { useDispatch } from 'react-redux';

const { Title } = Typography;
const { TextArea } = Input;

interface EditSellerInfoFormProps {
  user: User;
}

function EditSellerInfoForm({ user }: EditSellerInfoFormProps) {
  const dispatch = useDispatch();
  const { loginUser } = useAppSelector((state) => state.user);

  const [shippingMethods, setShippingMethods] = useState<Shipping[]>(user.default_shipping_methods);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleFinish = ({
    defaultPayment,
    defaultReturnPolicy,
  }: {
    defaultPayment: string;
    defaultReturnPolicy: string;
  }) => {
    if (isLoading) return;

    setIsLoading(true);
    patchUser(user.id, {
      default_shipping_methods: shippingMethods,
      default_payment: defaultPayment,
      default_return_policy: defaultReturnPolicy,
    })
      .then((updatedUser) => {
        if (updatedUser.id === loginUser?.id) dispatch(setUserState(updatedUser));
      })
      .catch((errMsg) => {
        if (errMsg) message.error(errMsg);
      })
      .finally(() => setIsLoading(false));
  };

  /**
   * Add shipping method.
   * @param newShippingMethod New shipping method (assume not empty, and not duplicated)
   */
  const createShippingMethod = (newShippingMethod: Shipping) => {
    // cost is nullable in front-end, but not in back-end
    if (newShippingMethod.cost === null) newShippingMethod.cost = 0;
    setShippingMethods([...shippingMethods, newShippingMethod]);
  };

  const updateShippingMethod = (keyDescription: string, newShippingMethod: Shipping) => {
    // cost is nullable in front-end, but not in back-end
    if (newShippingMethod.cost === null) newShippingMethod.cost = 0;

    // assume shipping type does not changed
    const type = newShippingMethod.type;

    setShippingMethods(
      shippingMethods.map((shippingMethod) =>
        shippingMethod.type === type && shippingMethod.description === keyDescription
          ? newShippingMethod
          : shippingMethod
      )
    );
  };

  const removeShippingMethod = (type: ShippingWay, keyDescription: string) =>
    setShippingMethods(
      shippingMethods.filter((method) => method.type !== type || method.description !== keyDescription)
    );

  return (
    <div className={styles.container}>
      <Form layout="vertical" onFinish={handleFinish}>
        <Title level={5}>기본 배송 정보</Title>
        <p className={`${styles['description']}`}>상품 등록 시 기본으로 입력될 배송 옵션입니다</p>
        <Segment className={styles['shipping-form-segment']}>
          <ShippingForm
            type="in_person"
            label="직거래 장소"
            insertMessage="+ 직거래 가능 시, 여기를 눌러 가능한 직거래 장소를 추가해주세요"
            shippingMethods={shippingMethods}
            createShippingMethod={createShippingMethod}
            updateShippingMethod={updateShippingMethod}
            removeShippingMethod={removeShippingMethod}
          />

          <ShippingForm
            type="parcel"
            label="배송 정보"
            insertMessage="+ 배송 가능 시, 여기를 눌러 배송 정보를 추가해주세요"
            shippingMethods={shippingMethods}
            createShippingMethod={createShippingMethod}
            updateShippingMethod={updateShippingMethod}
            removeShippingMethod={removeShippingMethod}
          />
        </Segment>

        <Title level={5}>기본 결제 정보</Title>
        <p className={`${styles['description']}`}>상품 등록 시 기본으로 입력될 결제 정보입니다</p>
        <Form.Item name="defaultPayment" initialValue={user.default_payment ?? ''}>
          <Input placeholder="천원은행 123-456-789012 홍길동" />
        </Form.Item>

        <Title level={5}>기본 반품 정보</Title>
        <p className={`${styles['description']}`}>상품 등록 시 기본으로 입력될 반품 안내 문구입니다</p>
        <Form.Item name="defaultReturnPolicy" initialValue={user.default_return_policy ?? ''}>
          <TextArea />
        </Form.Item>

        <div className={styles.end}>
          <Button type="primary" htmlType="submit" loading={isLoading} disabled={isLoading}>
            저장
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default memo(EditSellerInfoForm);
